import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import FieldPulse from "~/services/fieldpulse";
import Engage from "~/services/engage";
import NumbersContext from "~/contexts/NumbersContext";
import useDraft from "~/hooks/useDraft";
import { filterCustomerWithRelations } from "../helpers/customers";

const NumberContext = createContext({
  loading: false,
  saving: false,
  error: false,
  number: null,
});

export function NumberProvider({ value = {}, children }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fullNumber, setFullNumber] = useState(null);
  const [fullNumberError, setFullNumberError] = useState(null);
  const [number, setNumber] = useState(value);

  const { draft, dirty, updateDraft, resetDraft } = useDraft(number);

  const { getNumbers } = useContext(NumbersContext);

  const getNumber = useCallback(async () => {
    if (loading) return { response: number };
    setLoading(true);
    const res = await Engage.getNumber(number?.id);
    if (!res.error) {
      setNumber(res.response);
    } else {
      setError(res.message);
    }
    setLoading(false);
    return res;
  }, [number, loading, setError, setNumber, setSaving]);

  const updateNumber = useCallback(
    (data) => updateDraft(data),
    [number, updateDraft]
  );

  const saveNumber = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const res = await Engage.updateNumber(draft);
    if (!res.error) {
      await getNumber();
      resetDraft();
    } else {
      setError(res.message);
    }
    setSaving(false);
    await getNumbers();
    return res;
  }, [draft, saving, setSaving, setError, setNumber]);

  useEffect(() => {
    if (!fullNumber && !fullNumberError && number) {
      Engage.getNumber(number.id)
        .then((res) => {
          if (res.response) {
            setFullNumber(res.response);
          }
        })
        .catch((err) => setFullNumberError(err.message));
    }
  }, [fullNumber, number, draft]);

  useEffect(() => {
    if (fullNumber && !fullNumberError) {
      setNumber(fullNumber);
      resetDraft();
    }
  }, [fullNumber]);

  useEffect(() => {
    setNumber(value);
    setFullNumber(false);
  }, [value]);

  return (
    <NumberContext.Provider
      value={{
        number: {
          ...number,
          ...draft,
        },
        fullNumber,
        dirty,
        loading,
        saving,
        error,
        getNumber,
        setNumber,
        updateNumber,
        saveNumber,
      }}
    >
      {children}
    </NumberContext.Provider>
  );
}

export default NumberContext;
